import { Form } from "antd";
import { useEffect, useMemo } from "react";
import { EExtendsMergeStrategy, EServiceTemplateDataProcessingInCountriesSpecialTreatment, EServiceTemplateGroup, EServiceTemplateLegalBasis, ServiceTemplateField, TemplateMetaDataField, getServiceCdnState } from "@devowl-wp/api-real-cookie-banner";
/**
 * Hook to determine if the CDN field should be hidden based on the service template's CDN state and merge strategies.
 *
 * This method calculates the CDN state based on two main factors:
 * 1. Whether CDN is enabled (either directly or through inheritance)
 * 2. Whether special treatments for data processing are applied
 *
 * The possible states are:
 * - "disabled": CDN is not enabled
 * - "enabled": CDN is enabled without special treatments
 * - "enabledWithStandardClauses": CDN is enabled with special treatments
 *
 * @param watchFieldName - The name of the field to check for CDN state.
 * @returns The CDN field state.
 */ function useServiceCdnState(watchFieldName) {
    const form = Form.useFormInstance();
    const isCdn = Form.useWatch(ServiceTemplateField.IsCdn, form);
    const isCdnMergeStrategy = Form.useWatch([
        TemplateMetaDataField.ExtendedMergeStrategies,
        ServiceTemplateField.IsCdn
    ], form);
    const isEmbeddingOnlyExternalResources = Form.useWatch(ServiceTemplateField.IsEmbeddingOnlyExternalResources, form);
    const isEmbeddingOnlyExternalResourcesMergeStrategy = Form.useWatch([
        TemplateMetaDataField.ExtendedMergeStrategies,
        ServiceTemplateField.IsEmbeddingOnlyExternalResources
    ], form);
    const extended = Form.useWatch(TemplateMetaDataField.Extends, form);
    const specialTreatments = Form.useWatch(ServiceTemplateField.DataProcessingInCountriesSpecialTreatments, form);
    const isSpecialTreatmentEnabled = specialTreatments?.includes(EServiceTemplateDataProcessingInCountriesSpecialTreatment.StandardContractualClauses);
    const specialTreatmentMergeStrategy = Form.useWatch([
        TemplateMetaDataField.ExtendedMergeStrategies,
        ServiceTemplateField.DataProcessingInCountriesSpecialTreatments
    ], form);
    const targetFieldMergeStrategy = Form.useWatch([
        TemplateMetaDataField.ExtendedMergeStrategies,
        watchFieldName
    ], form);
    const cdnFieldState = useMemo(()=>{
        const isCdnEnabled = extended?.isCdn && isCdnMergeStrategy === EExtendsMergeStrategy.Keep || isCdn;
        const isEmbeddingOnlyExternalResourcesEnabled = extended?.isEmbeddingOnlyExternalResources && isEmbeddingOnlyExternalResourcesMergeStrategy === EExtendsMergeStrategy.Keep || (isEmbeddingOnlyExternalResources === null ? true : isEmbeddingOnlyExternalResources);
        const isSpecialTreatment = isSpecialTreatmentEnabled || extended?.dataProcessingInCountriesSpecialTreatments?.includes(EServiceTemplateDataProcessingInCountriesSpecialTreatment.StandardContractualClauses) && specialTreatmentMergeStrategy === EExtendsMergeStrategy.Keep;
        return getServiceCdnState(isCdnEnabled, isSpecialTreatment, isEmbeddingOnlyExternalResourcesEnabled);
    }, [
        isCdn,
        isCdnMergeStrategy,
        specialTreatments,
        isSpecialTreatmentEnabled,
        extended,
        specialTreatmentMergeStrategy,
        targetFieldMergeStrategy,
        isEmbeddingOnlyExternalResources,
        isEmbeddingOnlyExternalResourcesMergeStrategy
    ]);
    useEffect(()=>{
        if (cdnFieldState === "enabledWithStandardClauses") {
            form.setFieldValue(ServiceTemplateField.Group, EServiceTemplateGroup.Essential);
            if (form.getFieldValue(TemplateMetaDataField.Extends)) {
                const mergeStrategies = form.getFieldValue(TemplateMetaDataField.ExtendedMergeStrategies);
                mergeStrategies[watchFieldName] = EExtendsMergeStrategy.Overwrite;
                form.setFieldValue(TemplateMetaDataField.ExtendedMergeStrategies, mergeStrategies);
            }
            form.setFieldValue(ServiceTemplateField.LegalBasis, EServiceTemplateLegalBasis.LegitimateInterest);
        }
    }, [
        cdnFieldState
    ]);
    return cdnFieldState;
}
export { useServiceCdnState };
